import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../config/supabase';
import { storageService } from '../../services/storageService';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import FileUpload from '../../components/ui/FileUpload';

interface BusinessData {
  id: string;
  name: string;
  address: string;
  phone: string;
  logo_url: string | null;
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'general' | 'account' | 'business'>('account');
  const { user, businessId } = useAuth();

  // Account state
  const [displayName, setDisplayName] = useState('');
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountMessage, setAccountMessage] = useState('');

  // Business state
  const [businessData, setBusinessData] = useState<BusinessData | null>(null);
  const [businessName, setBusinessName] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [businessMessage, setBusinessMessage] = useState('');

  // Load account data
  useEffect(() => {
    if (user?.user_metadata?.name) {
      setDisplayName(user.user_metadata.name);
    }
  }, [user]);

  // Load business data
  useEffect(() => {
    const loadBusinessData = async () => {
      if (!businessId) return;

      try {
        const { data, error } = await supabase
          .from('businesses')
          .select('*')
          .eq('id', businessId)
          .single();

        if (error) throw error;

        setBusinessData(data);
        setBusinessName(data.name);
        setBusinessPhone(data.phone);
        setBusinessAddress(data.address);
        setLogoPreview(data.logo_url);
      } catch (error) {
        console.error('Error loading business data:', error);
      }
    };

    loadBusinessData();
  }, [businessId]);

  const handleAccountSave = async () => {
    try {
      setAccountLoading(true);
      setAccountMessage('');

      const { error } = await supabase.auth.updateUser({
        data: { name: displayName }
      });

      if (error) throw error;

      setAccountMessage('Account information updated successfully!');
    } catch (error) {
      console.error('Error updating account:', error);
      setAccountMessage('Failed to update account information.');
    } finally {
      setAccountLoading(false);
    }
  };

  const handleLogoSelect = (file: File) => {
    setLogoFile(file);
    const previewUrl = URL.createObjectURL(file);
    setLogoPreview(previewUrl);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove all non-numeric characters except the +62 prefix
    const value = e.target.value.replace(/[^\d]/g, '');

    // Ensure it starts with the Indonesian country code
    if (value.startsWith('62')) {
      setBusinessPhone('+' + value);
    } else if (value.startsWith('0')) {
      // Convert leading 0 to +62
      setBusinessPhone('+62' + value.substring(1));
    } else if (value) {
      setBusinessPhone('+62' + value);
    } else {
      setBusinessPhone('');
    }
  };

  const handleBusinessSave = async () => {
    if (!businessId) return;

    try {
      setBusinessLoading(true);
      setBusinessMessage('');

      let logoUrl = businessData?.logo_url || null;

      // Upload new logo if selected
      if (logoFile) {
        const { url, error: uploadError } = await storageService.uploadBusinessLogo(
          logoFile,
          businessId
        );

        if (uploadError) throw uploadError;
        logoUrl = url;
      }

      // Update business data
      const { error } = await supabase
        .from('businesses')
        .update({
          name: businessName,
          phone: businessPhone,
          address: businessAddress,
          logo_url: logoUrl,
        })
        .eq('id', businessId);

      if (error) throw error;

      setBusinessMessage('Business information updated successfully!');
      setBusinessData(prev => prev ? {
        ...prev,
        name: businessName,
        phone: businessPhone,
        address: businessAddress,
        logo_url: logoUrl,
      } : null);
    } catch (error) {
      console.error('Error updating business:', error);
      setBusinessMessage('Failed to update business information.');
    } finally {
      setBusinessLoading(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'General', disabled: true },
    { id: 'account', label: 'Account' },
    { id: 'business', label: 'Business' },
  ];

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : tab.disabled
                    ? 'border-transparent text-gray-400 cursor-not-allowed'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                disabled={tab.disabled}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'general' && (
            <div className="text-center py-12">
              <p className="text-gray-500">General settings coming soon...</p>
            </div>
          )}

          {activeTab === 'account' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Account Information</h2>

              {/* Profile Picture */}
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-xl font-medium text-white">
                    {user?.email?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Profile Picture</h3>
                  <p className="text-sm text-gray-500">Profile picture feature coming soon</p>
                </div>
              </div>

              {/* Display Name */}
              <Input
                label="Display Name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your display name"
              />

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="input-field bg-gray-50 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Email cannot be changed from settings
                </p>
              </div>

              {accountMessage && (
                <div className={`p-4 rounded-md ${
                  accountMessage.includes('successfully')
                    ? 'bg-green-50 text-green-800'
                    : 'bg-red-50 text-red-800'
                }`}>
                  {accountMessage}
                </div>
              )}

              <Button
                onClick={handleAccountSave}
                variant="primary"
                disabled={accountLoading}
              >
                {accountLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}

          {activeTab === 'business' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Business Information</h2>

              {/* Business Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Logo
                </label>
                <FileUpload
                  onFileSelect={handleLogoSelect}
                  currentImageUrl={logoPreview || undefined}
                  placeholder="Upload your business logo"
                  maxSize={2}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Recommended: PNG or JPG, max 2MB, square aspect ratio
                </p>
              </div>

              {/* Business Name */}
              <Input
                label="Business Name"
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Enter your business name"
              />

              {/* Business Phone - Indonesian format */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Phone
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">🇮🇩 +62</span>
                  </div>
                  <input
                    type="tel"
                    value={businessPhone.replace('+62', '')}
                    onChange={handlePhoneChange}
                    className="input-field pl-16"
                    placeholder="81234567890"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Enter your Indonesian phone number without the country code
                </p>
              </div>

              {/* Business Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Address
                </label>
                <textarea
                  value={businessAddress}
                  onChange={(e) => setBusinessAddress(e.target.value)}
                  rows={3}
                  className="input-field resize-vertical"
                  placeholder="Enter your business address"
                />
              </div>

              {businessMessage && (
                <div className={`p-4 rounded-md ${
                  businessMessage.includes('successfully')
                    ? 'bg-green-50 text-green-800'
                    : 'bg-red-50 text-red-800'
                }`}>
                  {businessMessage}
                </div>
              )}

              <Button
                onClick={handleBusinessSave}
                variant="primary"
                disabled={businessLoading || !businessName.trim()}
              >
                {businessLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;